import { useEffect, useRef, useState } from 'preact/hooks';
import type { PDFDocumentProxy } from 'pdfjs-dist';

// Renders pages of a scan batch PDF to canvases. pdf.js loads on first use
// (its own chunk), each document is fetched once per batch file, and each
// page renders only when it scrolls into view.

const docs = new Map<string, Promise<PDFDocumentProxy>>();

async function loadDoc(batchId: string, fileKey: string): Promise<PDFDocumentProxy> {
  const key = `${batchId}:${fileKey}`;
  let p = docs.get(key);
  if (!p) {
    p = (async () => {
      const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      const res = await fetch(`/api/t/batches/${batchId}/pdf`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Could not load the scan (${res.status})`);
      const data = new Uint8Array(await res.arrayBuffer());
      return pdfjs.getDocument({ data }).promise;
    })();
    p.catch(() => docs.delete(key));
    docs.set(key, p);
  }
  return p;
}

function PdfPage({ batchId, fileKey, page, width }: { batchId: string; fileKey: string; page: number; width: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<void> } | null = null;
    (async () => {
      try {
        const doc = await loadDoc(batchId, fileKey);
        if (cancelled || page > doc.numPages) return;
        const pg = await doc.getPage(page);
        const canvas = ref.current;
        if (cancelled || !canvas) return;
        const base = pg.getViewport({ scale: 1 });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = pg.getViewport({ scale: (width / base.width) * dpr });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        task = pg.render({ canvas, viewport });
        await task.promise;
      } catch (e) {
        if (!cancelled && (e as Error)?.name !== 'RenderingCancelledException') setErr((e as Error).message ?? 'Could not render');
      }
    })();
    return () => { cancelled = true; task?.cancel(); };
  }, [visible, batchId, fileKey, page, width]);

  return (
    <figure class="flex flex-col gap-1 min-w-0" style={{ width: `${width}px`, maxWidth: '100%' }}>
      <canvas ref={ref} aria-label={`Page ${page}`} class="w-full h-auto rounded-md border border-[var(--color-border)] bg-[#ffffff]" style={{ aspectRatio: 'auto 1 / 1.414' }} />
      <figcaption class="text-[11px] text-[var(--color-text-faint)]">{err ? `Page ${page}: ${err}` : `Page ${page}`}</figcaption>
    </figure>
  );
}

/** Pages first..last of a batch. fileKey (the file's sha) busts the cache
 *  when the batch file is replaced. */
export function PdfPages({ batchId, fileKey, first, last, width = 360, class: cls }: {
  batchId: string; fileKey: string; first: number; last: number; width?: number; class?: string;
}) {
  const pages: number[] = [];
  for (let p = first; p <= last; p++) pages.push(p);
  return (
    <div class={'flex flex-wrap gap-3 ' + (cls ?? '')}>
      {pages.map((p) => <PdfPage key={`${fileKey}:${p}`} batchId={batchId} fileKey={fileKey} page={p} width={width} />)}
    </div>
  );
}
